<template>
  <v-container>
    <!-- Document Header -->

    <v-toolbar class="mb-4" flat>
      <div>
        <h1 class="text-h4 font-weight-light mb-1">Comprobante de Ingreso</h1>
        <div class="text-caption grey--text">
          Gestión de documentos de ingreso • {{ formatDateHeader(issueDate) }}
        </div>
      </div>
      <v-spacer></v-spacer>
      {{ getCurrentDate() }}
    </v-toolbar>
    <!-- Main Document Card -->
    <v-card outlined>
      <!-- Header Section: Store (Left) - Client (Right) -->
      <v-container fluid class="pa-0">
        <v-row no-gutters>
          <!-- Store Information (Left) -->
          <v-col cols="12" md="6">
            <v-card flat tile class="pa-4" style="border-right: 1px solid #e0e0e0">
              <v-card-subtitle class="pa-0 mb-2">
                <v-icon color="primary" class="mr-2">mdi-store</v-icon>
                <span class="text-uppercase font-weight-bold">EMISOR</span>
              </v-card-subtitle>
              <v-card-text class="pa-0">
                <div class="text-h6 font-weight-bold">{{ seller.PartyName }}</div>
                <div class="text-body-1 font-weight-medium">
                  NIT: {{ seller.PartyIdentification }}
                </div>
                <div class="text-body-2">{{ seller.AddressLine }}</div>
                <div class="text-body-2">{{ seller.CityName }}</div>
                <div v-if="seller.client_phone" class="text-body-2">
                  Tel: {{ seller.client_phone }}
                </div>
                <div v-if="seller.client_email" class="text-body-2">
                  Email: {{ seller.client_email }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Client Information (Right) -->
          <v-col cols="12" md="6">
            <v-card flat tile class="pa-4" height="100%">
              <v-card-subtitle
                class="pa-0 mb-2 d-flex justify-space-between align-center"
              >
                <div>
                  <v-icon color="success" class="mr-2">mdi-account-cash</v-icon>
                  <span class="text-uppercase font-weight-bold">RECIBIDO DE</span>
                </div>
                <v-btn color="success" text small @click="client_dialog = true">
                  <v-icon left small>mdi-pencil</v-icon>
                  {{ client.PartyName ? "Editar" : "Seleccionar" }}
                </v-btn>
              </v-card-subtitle>
              <v-card-text class="pa-0">
                <div v-if="client.PartyName">
                  <div class="text-h6 font-weight-bold">{{ client.PartyName }}</div>
                  <div class="text-body-1 font-weight-medium">
                    {{ get_name_typeId(client.typeid) }}: {{ client.PartyIdentification }}
                  </div>
                  <div v-if="client.AddressLine" class="text-body-2">
                    {{ client.AddressLine }}
                  </div>
                  <div v-if="client.CityName" class="text-body-2">
                    {{ getCityName(client.CityName) }}
                  </div>
                  <div v-if="client.client_phone" class="text-body-2">
                    Tel: {{ client.client_phone }}
                  </div>
                  <div v-if="client.client_email" class="text-body-2">
                    Email: {{ client.client_email }}
                  </div>
                </div>
                <v-alert v-else type="info" text>
                  <div class="text-center">
                    <v-icon size="32">mdi-account-question</v-icon>
                    <div class="mt-2">Seleccionar cliente</div>
                    <v-btn
                      color="success"
                      small
                      outlined
                      @click="client_dialog = true"
                      class="mt-2"
                    >
                      <v-icon left small>mdi-account-plus</v-icon>
                      Seleccionar
                    </v-btn>
                  </div>
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-divider></v-divider>

      <!-- Items Section -->
      <v-container fluid class="pa-4">
        <v-row>
          <v-col cols="12">
            <v-card-subtitle class="pa-0 mb-3 d-flex justify-space-between align-center">
              <div>
                <v-icon color="orange" class="mr-2">mdi-text-box-multiple</v-icon>
                <span class="text-uppercase font-weight-bold"
                  >CONCEPTOS DEL COMPROBANTE</span
                >
              </div>
              <v-btn color="orange" @click="concepts_dialog = true">
                <v-icon left>mdi-plus</v-icon>
                Agregar Concepto
              </v-btn>
            </v-card-subtitle>

            <v-data-table
              :headers="headers"
              :items="concepts"
              :items-per-page="-1"
              hide-default-footer
              :loading="loading_status"
              loading-text="Cargando conceptos..."
            >
              <template v-slot:[`item.item_concept`]="{ item }">
                <div class="d-flex align-center">
                  <v-icon small color="orange" class="mr-2">mdi-tag</v-icon>
                  <span class="font-weight-medium">{{ item.item_concept }}</span>
                </div>
              </template>

              <template v-slot:[`item.item_note`]="{ item }">
                <span class="text-truncate" style="max-width: 300px">
                  {{ item.item_note }}
                </span>
              </template>

              <template v-slot:[`item.item_reference`]="{ item }">
                <v-chip small outlined>
                  <v-icon left x-small>mdi-file-document</v-icon>
                  {{ item.item_reference }}
                </v-chip>
              </template>

              <template v-slot:[`item.item_value`]="{ item }">
                <span class="text-h6 font-weight-bold primary--text">
                  {{ formatCurrency(item.item_value) }}
                </span>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon small @click="delete_concept(item)">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </template>

              <template v-slot:body.append v-if="concepts.length > 0">
                <tr class="grey lighten-4">
                  <td colspan="3" class="text-right font-weight-bold text-uppercase">
                    Subtotal Conceptos
                  </td>
                  <td class="text-right">
                    <span class="text-h6 font-weight-bold primary--text">
                      {{ formatCurrency(sumField(concepts, "item_value")) }}
                    </span>
                  </td>
                  <td></td>
                </tr>
              </template>

              <template v-slot:no-data>
                <v-container class="text-center py-8">
                  <v-icon size="64" color="grey lighten-2">mdi-text-box-outline</v-icon>
                  <div class="text-h6 mt-4 grey--text">No hay conceptos agregados</div>
                  <div class="text-caption grey--text mt-2">
                    Haz clic en "Agregar Concepto" para incluir conceptos al comprobante
                  </div>
                </v-container>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>

      <v-divider></v-divider>

      <!-- Bottom Section: Payments (Left) - Totals (Right) -->
      <v-container fluid class="pa-0">
        <v-row no-gutters style="min-height: 250px">
          <!-- Payment Methods (Left) -->
          <v-col
            cols="12"
            md="7"
            class="pa-4 d-flex flex-column"
            style="border-right: 1px solid #e0e0e0"
          >
            <div class="d-flex justify-space-between align-center mb-4">
              <div>
                <v-icon color="success" class="mr-2">mdi-cash-multiple</v-icon>
                <span class="text-uppercase font-weight-bold">FORMAS DE PAGO</span>
              </div>
              <v-btn
                color="success"
                small
                @click="payment_dialogo = true"
                :disabled="concepts.length === 0"
              >
                <v-icon left small>mdi-plus</v-icon>
                Agregar
              </v-btn>
            </div>

            <div class="flex-grow-1">
              <v-simple-table v-if="payments.length > 0">
                <tbody>
                  <tr v-for="(payment, index) in payments" :key="index">
                    <td class="text-left">
                      <div class="d-flex align-center">
                        <v-icon
                          small
                          class="mr-2"
                          :color="getPaymentColor(payment.payment_code)"
                        >
                          {{ getPaymentIcon(payment.payment_code) }}
                        </v-icon>
                        <div>
                          <div class="font-weight-medium">
                            {{ payment.payment_concept }}
                          </div>
                          <div class="text-caption grey--text">
                            Ref: {{ payment.payment_reference }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="text-right font-weight-bold success--text">
                      {{ formatCurrency(payment.payment_value) }}
                    </td>
                    <td class="text-right" style="width: 50px">
                      <v-btn icon small @click="delete_payment(payment)">
                        <v-icon small color="error">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-alert v-else type="info" text>
                <div class="text-center">
                  <v-icon>mdi-cash-remove</v-icon>
                  <div class="mt-1">
                    {{
                      concepts.length === 0
                        ? "Agregue conceptos primero"
                        : "No hay pagos registrados"
                    }}
                  </div>
                </div>
              </v-alert>
            </div>
          </v-col>

          <!-- Totals Summary (Right) -->
          <v-col cols="12" md="5" class="pa-4 d-flex flex-column">
            <div class="mb-4">
              <v-icon color="primary" class="mr-2">mdi-calculator</v-icon>
              <span class="text-uppercase font-weight-bold">RESUMEN DE TOTALES</span>
            </div>

            <div class="flex-grow-1 d-flex flex-column">
              <v-simple-table>
                <tbody>
                  <tr>
                    <td class="text-left">
                      <v-icon small class="mr-2" color="orange">mdi-sigma</v-icon>
                      Total Conceptos
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(sumField(concepts, "item_value")) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="text-left">
                      <v-icon small class="mr-2" color="success">mdi-cash-check</v-icon>
                      Total Pagado
                    </td>
                    <td class="text-right font-weight-bold success--text">
                      {{ formatCurrency(sumField(payments, "payment_value")) }}
                    </td>
                  </tr>
                  <tr class="grey lighten-3">
                    <td class="text-left">
                      <v-icon small class="mr-2" :color="getBalanceColor()">
                        {{ getBalanceIcon() }}
                      </v-icon>
                      <strong>Saldo Pendiente</strong>
                    </td>
                    <td class="text-right font-weight-bold" :class="getBalanceClass()">
                      {{ formatCurrency(getBalance()) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-alert
                :type="
                  getBalance() === 0 ? 'success' : getBalance() < 0 ? 'error' : 'warning'
                "
                text
                dense
                class="mt-3 mb-3"
              >
                <div class="d-flex align-center">
                  <v-icon class="mr-2">{{ getBalanceIcon() }}</v-icon>
                  {{ getBalanceText() }}
                </div>
              </v-alert>

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                large
                block
                :disabled="!isReady()"
                :loading="loading_status"
                @click="save_receipt"
                class="mt-auto"
              >
                <v-icon left>mdi-printer</v-icon>
                Emitir Comprobante
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Status Chip (Top Right) -->
    <v-chip v-if="isReady()" color="success" outlined class="status-chip">
      <v-icon left small>mdi-check-circle</v-icon>
      Listo para emitir
    </v-chip>
    <v-chip
      v-else-if="concepts.length > 0 || payments.length > 0"
      color="warning"
      outlined
      class="status-chip"
    >
      <v-icon left small>mdi-progress-clock</v-icon>
      En progreso
    </v-chip>

    <!-- Client Dialog -->
    <v-dialog v-model="client_dialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon class="mr-2" color="white">mdi-account-cash</v-icon>
          Información del Cliente
          <v-spacer></v-spacer>
          <v-btn icon dark @click="client_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="client.typeid"
                :items="setting['type_document_identifications']"
                item-value="id"
                item-text="name"
                label="Tipo de documento"
                outlined
                dense
                prepend-inner-icon="mdi-card-account-details"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="client.PartyIdentification"
                label="Número de documento"
                outlined
                dense
                clearable
                autofocus
                @keyup.enter="get_rut"
                @keypress="filter"
                @click:clear="clearClient"
                prepend-inner-icon="mdi-identifier"
              >
                <template v-slot:append-outer>
                  <v-btn icon color="primary" @click="get_rut" :loading="loading_status">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="client.PartyName"
                label="Nombre completo / Razón social"
                outlined
                dense
                prepend-inner-icon="mdi-account"
              />
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="client.client_email"
                label="Correo electrónico"
                outlined
                dense
                type="email"
                prepend-inner-icon="mdi-email"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="client.client_phone"
                label="Teléfono"
                outlined
                dense
                @keypress="filter"
                prepend-inner-icon="mdi-phone"
              />
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="client.AddressLine"
                label="Dirección"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-autocomplete
                v-model="client.CityName"
                :items="setting['municipalities']"
                item-value="id"
                item-text="name"
                label="Ciudad"
                outlined
                dense
                prepend-inner-icon="mdi-city"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="client_dialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="save_client" :loading="loading_status">
            <v-icon left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Concepts Dialog -->
    <v-dialog v-model="concepts_dialog" max-width="700px" persistent>
      <v-form ref="conceptForm" v-model="validConcept" @submit.prevent="add2concepts">
        <v-card>
          <v-card-title class="orange white--text">
            <v-icon class="mr-2" color="white">mdi-text-box-plus</v-icon>
            Agregar Concepto
            <v-spacer></v-spacer>
            <v-btn icon dark @click="concepts_dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-6">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="concept.item_code"
                  :items="concept_lst"
                  item-value="codigo"
                  item-text="valor"
                  label="Tipo de concepto"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-tag-text"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="concept.item_note"
                  label="Descripción / Observación"
                  :rules="f_required"
                  outlined
                  dense
                  rows="2"
                  autofocus
                  prepend-inner-icon="mdi-note-text"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="concept.item_reference"
                  label="Documento de referencia"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-file-document"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="concept.item_value"
                  label="Valor"
                  type="number"
                  step="0.01"
                  :rules="[...f_required, validateAmount]"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-eur"
                >
                  <template v-slot:append>
                    <span class="text-caption">EUR</span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="concepts_dialog = false">Cancelar</v-btn>
            <v-btn color="orange" type="submit" :disabled="!validConcept">
              <v-icon left>mdi-plus</v-icon>
              Agregar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Payment Dialog -->
    <v-dialog v-model="payment_dialogo" max-width="700px" persistent>
      <v-form ref="payform" v-model="payvalid" @submit.prevent="addPayment">
        <v-card>
          <v-card-title class="success white--text">
            <v-icon class="mr-2" color="white">mdi-cash-plus</v-icon>
            Registrar Pago
            <v-spacer></v-spacer>
            <v-btn icon dark @click="payment_dialogo = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-6">
            <v-alert v-if="totals.unpaid > 0" type="info" text dense class="mb-4">
              <div class="d-flex align-center justify-space-between">
                <span>Saldo pendiente por pagar:</span>
                <span class="font-weight-bold">{{ formatCurrency(totals.unpaid) }}</span>
              </div>
            </v-alert>

            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="pay_mode"
                  :items="FP"
                  return-object
                  item-value="codigo"
                  item-text="valor"
                  label="Forma de pago"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-credit-card"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="payment.payment_reference"
                  label="Referencia / Número de documento"
                  :rules="f_required"
                  :error="payRefe"
                  @input="updateRefe"
                  outlined
                  dense
                  prepend-inner-icon="mdi-file-document"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="payment.payment_value"
                  label="Valor"
                  type="number"
                  step="0.01"
                  :rules="[...f_required, validatePaymentAmount]"
                  :error="payValue"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-eur"
                >
                  <template v-slot:append>
                    <span class="text-caption">EUR</span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="payment_dialogo = false">Cancelar</v-btn>
            <v-btn color="success" type="submit" :disabled="!payvalid">
              <v-icon left>mdi-cash-plus</v-icon>
              Registrar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Print Drawer -->
    <v-navigation-drawer v-model="showPrint" right app temporary width="400">
      <v-card height="100%">
        <v-toolbar color="primary" dark>
          <v-toolbar-title>Comprobante de Ingreso</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="showPrint = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-img :src="dataURL" contain></v-img>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn block large color="primary" @click="print_doc">
            <v-icon left>mdi-printer</v-icon>
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, fe_ws, getToday } from "@/services/webserver.js";
import createClient from "@/utils/createClient.js";
import createPayment from "@/utils/createPayment.js";
import create_item from "@/utils/create_item.js";
import settings from "@/utils/settings.js";

export default {
  name: "ReceiptIn",

  data() {
    return {
      // Settings and state
      setting: settings(),
      loading_status: false,
      issueDate: getToday(),

      // Dialogs
      client_dialog: false,
      payment_dialogo: false,
      concepts_dialog: false,
      showPrint: false,

      // Validation
      validConcept: false,
      payvalid: false,
      payRefe: false,
      payValue: false,

      // Main data
      seller: {},
      store: createClient(),
      client: createClient(),
      concepts: [],
      payments: [],
      concept: create_item(),
      payment: createPayment(),

      // Receipt and print
      receipt: null,
      signature: null,
      dataURL: "",

      // Totals
      totals: {
        qty: 0,
        total: 0,
        tax: 0,
        paid: 0,
        unpaid: 0,
      },

      // Lists
      concept_lst: [],
      FP: null,
      pay_mode: {
        codigo: "01",
        valor: "EFECTIVO",
      },

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Table headers
      headers: [
        {
          text: "Concepto",
          value: "item_concept",
          sortable: false,
          width: "25%",
        },
        {
          text: "Descripción",
          value: "item_note",
          sortable: false,
          width: "35%",
        },
        {
          text: "Referencia",
          value: "item_reference",
          align: "center",
          width: "20%",
        },
        {
          text: "Valor",
          value: "item_value",
          align: "right",
          width: "15%",
        },
        {
          text: "",
          value: "actions",
          align: "center",
          sortable: false,
          width: "5%",
        },
      ],

      headers_payments: [
        {
          text: "Forma de Pago",
          value: "payment_concept",
          sortable: false,
          width: "60%",
        },
        {
          text: "Valor",
          value: "payment_value",
          align: "right",
          width: "30%",
        },
        {
          text: "",
          value: "actions",
          align: "center",
          sortable: false,
          width: "10%",
        },
      ],

      // Validation rules
      f_required: [(v) => !!v || "Este campo es obligatorio"],
    };
  },

  mounted() {
    this.seller = window.seller;
    this.store = window.store;
    this.concept_lst = window.settings["INGRESOS"];
    this.FP = window.settings.FP;
  },

  methods: {
    formatDateHeader(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    // Date formatting
    getCurrentDate() {
      return new Date().toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    // Currency formatting
    formatCurrency(value) {
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    // Validation
    validateAmount(v) {
      if (!v) return true;
      return parseFloat(v) > 0 || "El valor debe ser mayor que cero";
    },

    validatePaymentAmount(v) {
      if (!v) return true;
      if (parseFloat(v) <= 0) return "El valor debe ser mayor que cero";
      if (
        this.pay_mode &&
        this.pay_mode.codigo !== "01" &&
        parseFloat(v) > this.totals.unpaid
      ) {
        return `El valor no puede superar ${this.formatCurrency(this.totals.unpaid)}`;
      }
      return true;
    },

    filter(evt) {
      evt = evt ? evt : window.event;
      let expect = evt.target.value.toString() + evt.key.toString();

      if (!/^[-+]?[0-9]*\.?[0-9]*$/.test(expect)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    // Balance calculations
    getBalance() {
      const total = this.sumField(this.concepts, "item_value");
      const paid = this.sumField(this.payments, "payment_value");
      return total - paid;
    },

    getBalanceClass() {
      const balance = this.getBalance();
      if (balance === 0) return "success--text";
      if (balance < 0) return "error--text";
      return "warning--text";
    },

    getBalanceColor() {
      const balance = this.getBalance();
      if (balance === 0) return "success";
      if (balance < 0) return "error";
      return "warning";
    },

    getBalanceIcon() {
      const balance = this.getBalance();
      if (balance === 0) return "mdi-check-circle";
      if (balance < 0) return "mdi-alert-circle";
      return "mdi-clock-alert";
    },

    getBalanceText() {
      const balance = this.getBalance();
      if (balance === 0) return "Balanceado";
      if (balance < 0) return "Sobrepago";
      return "Pendiente";
    },

    isReady() {
      return (
        this.concepts.length > 0 &&
        this.client.PartyIdentification &&
        this.getBalance() === 0
      );
    },

    // Utilities
    sumField(fld, key) {
      return fld.reduce((a, b) => parseFloat(a) + (parseFloat(b[key]) || 0), 0);
    },

    get_name_typeId(e) {
      const doc = this.setting["type_document_identifications"].find((d) => d.id === e);
      return doc ? doc.name : "Documento";
    },

    getCityName(cityId) {
      if (!cityId) return "No especificada";
      const city = this.setting["municipalities"].find((m) => m.id === cityId);
      return city ? city.name : cityId;
    },

    get_concept_text(e) {
      const concept = this.concept_lst.find((c) => c.codigo === e);
      return concept ? concept.valor : "";
    },

    containsOnlyNumbers(str) {
      return /^\d+$/.test(str);
    },

    getPaymentIcon(code) {
      const icons = {
        "01": "mdi-cash",
        "02": "mdi-credit-card",
        "03": "mdi-bank-transfer",
        "04": "mdi-checkbook",
      };
      return icons[code] || "mdi-cash-multiple";
    },

    getPaymentColor(code) {
      const colors = {
        "01": "success",
        "02": "primary",
        "03": "info",
        "04": "warning",
      };
      return colors[code] || "grey";
    },

    // Message handling
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Client management
    validateID(e) {
      if (e.keyCode === 13) {
        this.get_rut();
      }
    },

    clearClient() {
      this.client = createClient();
      this.client.CityName = window.seller.city_code;
    },

    get_rut() {
      if (!this.client.PartyIdentification) {
        this.showMessage("Ingrese un número de documento", "warning");
        return;
      }

      this.loading_status = true;
      const qry = {
        id_company: window.seller.PartyIdentification,
        method: "POST",
        nit: this.client.PartyIdentification,
        data: [],
      };

      fe_ws(
        "get_rut",
        qry,
        (data) => {
          this.loading_status = false;
          const rut = JSON.parse(data);

          if (rut.business_name) {
            this.client.PartyName = rut.business_name;
            this.client.typeid = "6";
            this.get_fe_email();
          } else if (rut.first_name) {
            this.client.PartyName = `${rut.first_name} ${rut.surname} ${
              rut.second_surname || ""
            }`.trim();
            this.client.typeid = "3";
            this.get_fe_email();
          } else {
            this.showMessage("No se encontró información del documento", "warning");
            this.clearClient();
          }
        },
        () => {
          this.loading_status = false;
          this.showMessage("Error al buscar el documento", "error");
        }
      );
    },

    get_fe_email() {
      this.loading_status = true;
      const qry = {
        id_company: window.seller.PartyIdentification,
        method: "POST",
        nit: this.client.PartyIdentification,
        data: [],
      };

      fe_ws(
        "get_rut_email",
        qry,
        (data) => {
          this.loading_status = false;
          const rut_email = JSON.parse(data);

          if (rut_email.email) {
            this.client.client_email = rut_email.email;
          }
          this.get_client(this.client.PartyIdentification, !rut_email.email);
        },
        () => {
          this.loading_status = false;
          this.get_client(this.client.PartyIdentification, true);
        }
      );
    },

    get_client(id, updateEmail) {
      const qry = {
        store: window.store.store_id,
        PartyIdentification: this.client.PartyIdentification,
      };

      webserver("get_client_info", qry, (data) => {
        if (data) {
          if (updateEmail && data.client_email) {
            this.client.client_email = data.client_email;
          }

          if (data.AddressLine) {
            this.client.AddressLine = data.AddressLine;
          }

          if (data.client_phone) {
            this.client.client_phone = data.client_phone;
          }

          if (data.CityName && this.containsOnlyNumbers(data.CityName)) {
            this.client.CityName = data.CityName;
          } else {
            this.client.CityName = window.seller.city_code;
          }
        }
      });
    },

    save_client() {
      if (!this.client.PartyName || !this.client.PartyIdentification) {
        this.showMessage("Complete los campos obligatorios", "error");
        return;
      }

      const qry = {
        store: window.store.store_id,
        party: this.client,
      };

      this.loading_status = true;

      webserver(
        "put_client_info",
        qry,
        () => {
          this.loading_status = false;
          this.client_dialog = false;
          this.showMessage("Cliente guardado correctamente", "success");
        },
        () => {
          this.loading_status = false;
          this.showMessage("Error al guardar el cliente", "error");
        }
      );
    },

    // Concepts management
    add2concepts() {
      this.$refs.conceptForm.validate();

      if (this.validConcept) {
        const newItem = { ...this.concept };
        newItem.item_value = parseFloat(parseFloat(newItem.item_value).toFixed(2));
        newItem.price = newItem.item_value;
        newItem.item_concept = this.get_concept_text(newItem.item_code);

        this.concepts.push(newItem);
        this.concepts_dialog = false;
        this.updateTotal();
        this.concept = create_item();

        this.$nextTick(() => {
          this.$refs.conceptForm.reset();
        });

        this.showMessage("Concepto agregado", "success");
      }
    },

    delete_concept(item) {
      const index = this.concepts.indexOf(item);
      if (index !== -1) {
        this.concepts.splice(index, 1);
        this.updateTotal();
        this.showMessage("Concepto eliminado", "info");
      }
    },

    // Payments management
    addPayment() {
      if (!this.pay_mode) {
        return;
      }

      this.payment.payment_value = parseFloat(
        parseFloat(this.payment.payment_value).toFixed(2)
      );

      if (this.pay_mode.codigo === "01") {
        // Cash payment
        this.payment.payment_concept = this.pay_mode.valor;
        this.payment.payment_code = this.pay_mode.codigo;
        this.payments.push({ ...this.payment });
        this.updateTotal();

        // Handle change if cash payment exceeds pending amount
        if (this.totals.unpaid < 0) {
          const cambio = {
            payment_concept: "CAMBIO EN EFECTIVO",
            payment_code: "01",
            payment_reference: "Cambio",
            payment_value: this.totals.unpaid,
          };
          this.payments.push({ ...cambio });
          this.updateTotal();
        }

        this.payment_dialogo = false;
        this.payment = createPayment();
        this.pay_mode = {
          codigo: "01",
          valor: "EFECTIVO",
        };

        this.showMessage("Pago registrado", "success");
      } else {
        // Other payment methods
        if (parseFloat(this.payment.payment_value) <= this.totals.unpaid) {
          this.payment.payment_concept = this.pay_mode.valor;
          this.payment.payment_code = this.pay_mode.codigo;
          this.payments.push({ ...this.payment });
          this.updateTotal();

          this.payment_dialogo = false;
          this.payment = createPayment();
          this.pay_mode = {
            codigo: "01",
            valor: "EFECTIVO",
          };

          this.showMessage("Pago registrado", "success");
        } else {
          this.payValue = true;
          this.showMessage("El valor excede el saldo pendiente", "warning");
        }
      }
    },

    updateRefe() {
      this.payRefe =
        !this.payment.payment_reference || this.payment.payment_reference.length === 0;
    },

    delete_payment(item) {
      const index = this.payments.indexOf(item);
      if (index !== -1) {
        this.payments.splice(index, 1);
        this.updateTotal();
        this.showMessage("Pago eliminado", "info");
      }
    },

    // Totals update
    updateTotal() {
      this.totals.qty = 0;
      this.totals.total = 0;
      this.totals.tax = 0;
      this.totals.paid = 0;
      this.totals.unpaid = 0;

      this.concepts.forEach((itm) => {
        this.totals.qty += parseFloat(itm.quantity || 1);
        this.totals.total += parseFloat(parseFloat(itm.item_value).toFixed(2));
      });

      this.payments.forEach((itm) => {
        this.totals.paid += parseFloat(parseFloat(itm.payment_value).toFixed(2));
      });

      this.totals.unpaid = parseFloat((this.totals.total - this.totals.paid).toFixed(2));
      this.payment.payment_value = this.totals.unpaid;
    },

    // Receipt management
    save_receipt() {
      if (this.concepts.length === 0) {
        this.showMessage("Debe agregar al menos un concepto", "error");
        return;
      }

      if (!this.client.PartyName || !this.client.PartyIdentification) {
        this.showMessage("Debe seleccionar un cliente", "error");
        return;
      }

      if (this.getBalance() !== 0) {
        this.showMessage("El total debe estar completamente pagado", "error");
        return;
      }

      this.loading_status = true;

      const qry = {
        doc: {
          doc_type: "CS-IN",
          doc_value: this.totals.paid,
          store: window.store.store_id,
          userId: window.profile.user_email,
        },
        items: this.concepts,
        payments: this.payments,
        paidTo: this.client,
        seller: window.seller,
        store: window.store.store_id,
      };

      console.log("Saving receipt data:", qry);

      webserver(
        "receipt_save",
        qry,
        (data) => {
          console.log("Receipt saved successfully:", data);
          this.loading_status = false;
          this.print_receipt(data);
          this.showMessage("Comprobante generado correctamente", "success");

          setTimeout(() => {
            this.resetDoc();
          }, 500);
        },
        () => {
          this.loading_status = false;
          this.showMessage("Error al generar el comprobante", "error");
        }
      );
    },

    resetDoc() {
      this.payments = [];
      this.concepts = [];
      this.client = createClient();
      this.client.CityName = window.seller.city_code;
      this.updateTotal();
    },

    print_doc() {
      const printWindow = window.open("", "", "height=600, width=400");
      printWindow.document.write('<img src="' + this.dataURL + '" width="100%">');
      printWindow.print();
      printWindow.document.close();
      setTimeout(() => {
        printWindow.close();
      }, 500);
    },

    print_receipt(data) {
      const LS = 50;
      const itemsQty = data.items.length * LS * 3;
      const elem = document.createElement("canvas");
      elem.width = 945;
      elem.height = 1800 + itemsQty;
      let total = 0;
      const center = elem.width / 2;
      const left = 10;
      let line = 150;
      const right = elem.width - left;
      const ctx = elem.getContext("2d");

      // Background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, elem.width, elem.height);
      ctx.fillStyle = "#000000";

      // Header
      ctx.font = "bold 60px Arial";
      ctx.textAlign = "center";
      ctx.fillText(window.seller.PartyIdentification_Id, center, line);
      line += LS;

      ctx.font = "bold 40px Arial";
      ctx.fillText(window.seller.PartyName, center, line);
      line += LS;

      ctx.fillText("Nit : " + window.seller.PartyIdentification, center, line);
      line += LS;

      ctx.fillText(
        window.seller.AddressLine + ", " + window.seller.CityName,
        center,
        line
      );
      line += LS;

      ctx.font = "bold 35px Arial";
      ctx.fillText("COMPROBANTE DE INGRESO A CAJA", center, line);
      line += LS;

      ctx.font = "bold 40px Arial";
      ctx.fillText("No. " + data.document.doc_number, center, line);
      line += LS;

      ctx.fillText(data.document.issueDate, center, line);
      line += LS;

      // Client info
      ctx.textAlign = "start";
      ctx.fillText("C.C./Nit : " + data.buyer.PartyIdentification, left, line);
      line += LS;

      ctx.fillText("Nombre   : " + data.buyer.PartyName, left, line);
      line += LS * 2;

      // Concepts
      ctx.font = "bold 40px Arial";
      ctx.textAlign = "center";
      ctx.fillText("CONCEPTOS", center, line);
      line += LS;

      ctx.beginPath();
      ctx.moveTo(left, line - 10);
      ctx.lineTo(right, line - 10);
      ctx.stroke();
      line += LS;

      // List concepts
      data.items.forEach((row) => {
        const price = parseFloat(row.price) * parseFloat(row.quantity || 1);

        ctx.textAlign = "start";
        ctx.fillText(row.item_concept, left, line);
        line += LS;

        ctx.fillText(row.item_note, left, line);
        ctx.textAlign = "end";

        ctx.fillText(
          new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
          }).format(price),
          right,
          line
        );

        line += LS;
        ctx.textAlign = "start";
        ctx.fillText("Ref: " + row.item_reference, left, line);

        line += LS;
        total += price;
      });

      ctx.beginPath();
      ctx.moveTo(left, line - 10);
      ctx.lineTo(right, line - 10);
      ctx.stroke();
      line += LS * 2;

      // Payment methods
      ctx.textAlign = "center";
      ctx.fillText("FORMAS DE PAGO", center, line);
      line += LS;

      data.payment.forEach((row) => {
        ctx.textAlign = "start";
        ctx.fillText(row.payment_concept + " - " + row.payment_reference, left, line);
        ctx.textAlign = "end";

        ctx.fillText(
          new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
          }).format(row.payment_value),
          right,
          line
        );

        line += LS;
      });

      // Total
      line += LS;
      ctx.beginPath();
      ctx.moveTo(left, line - 10);
      ctx.lineTo(right, line - 10);
      ctx.stroke();
      line += LS;

      ctx.textAlign = "start";
      ctx.font = "bold 40px Arial";
      ctx.fillText("TOTAL:", left, line);
      ctx.textAlign = "end";

      ctx.fillText(
        new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
        }).format(total),
        right,
        line
      );

      line += LS * 2;

      // Signature
      ctx.textAlign = "center";
      ctx.font = "bold 35px Arial";
      ctx.fillText("___________________________", center, line);
      line += LS;
      ctx.fillText("Firma", center, line);

      this.dataURL = elem.toDataURL();
      this.showPrint = true;
    },
  },
};
</script>

<style scoped>
.status-chip {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 5;
}
</style>
